import React, { useEffect, useRef, useState } from "react";

import styles from "./Banner.module.scss";
import {
  Banner,
  dismissBanner,
  getBanners,
} from "../../services/BannerService";

const BANNER_HEIGHT_VAR = "--banner-height";

const BannerBar = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const stackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getBanners()
      .then((data) => setBanners(data))
      .catch(() => setBanners([]));
  }, []);

  // Publish the rendered banner height to a CSS variable so every content
  // area in the app can subtract it and fit the viewport without scrolling.
  useEffect(() => {
    const root = document.documentElement;
    const el = stackRef.current;
    if (!el) {
      root.style.setProperty(BANNER_HEIGHT_VAR, "0px");
      return () => {};
    }
    const update = () =>
      root.style.setProperty(BANNER_HEIGHT_VAR, `${el.offsetHeight}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.setProperty(BANNER_HEIGHT_VAR, "0px");
    };
  }, [banners]);

  const handleDismiss = (id: number) => {
    setBanners((prev) => prev.filter((banner) => banner.id !== id));
    dismissBanner(id).catch(() => {
      /* best-effort: banner is already hidden locally */
    });
  };

  if (!banners.length) {
    return null;
  }

  return (
    <div className={styles.bannerStack} ref={stackRef}>
      {banners.map((banner) => (
        <div key={banner.id} className={styles.banner}>
          <div
            className={`${styles.bannerContent} ${
              banner.is_dismissible ? styles.dismissibleContent : ""
            }`}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: banner.content }}
          />
          {banner.is_dismissible && (
            <button
              type="button"
              aria-label="Dismiss banner"
              className={styles.dismiss}
              onClick={() => handleDismiss(banner.id)}
            >
              &times;
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default BannerBar;
